import * as Yup from 'yup';
import dayjs from 'dayjs';
import { UAE_COUNTRY_CODE } from '../constants/defaults';
import { transactionTypePositionCheck } from '../utils/helper';
import {
  createAgentBillingDistinctnessTest,
  createAgentBillingPartyDistinctnessTest,
} from './agentBillingDistinctnessValidation';

const getRootValue = (context) => {
  if (context.from && Array.isArray(context.from) && context.from.length > 0) {
    return context.from[context.from.length - 1].value;
  }

  return context.parent || {};
};

const isEcommerceProfile = (profileExecutionId) => transactionTypePositionCheck(profileExecutionId, 7);
const isExportProfile = (profileExecutionId) => transactionTypePositionCheck(profileExecutionId, 8);
const requiresDeliveryAddressFields = (profileExecutionId) =>
  isEcommerceProfile(profileExecutionId) || isExportProfile(profileExecutionId);

const getDeliveryCountryCode = (deliveryCountryCode) => {
  if (typeof deliveryCountryCode === 'object' && deliveryCountryCode !== null && 'code' in deliveryCountryCode) {
    return deliveryCountryCode.code;
  }

  return deliveryCountryCode;
};

export const createProfileTransactionTypeFields = (m, { isPurchase = false } = {}) => {
  const agentBillingPrincipleTest = createAgentBillingDistinctnessTest(isPurchase);

  return {
    principle_id_trn: Yup.string()
      .when('profile_execution_id', ([profileExecutionId], schema) => {
        if (transactionTypePositionCheck(profileExecutionId, 6)) {
          return schema.required(m.principle_id_trn_required);
        }

        return schema.notRequired().nullable();
      })
      .test('agent-billing-distinct', m.agent_billing_same_vat_and_principal, agentBillingPrincipleTest),

    beneficiary_id: Yup.string().when('profile_execution_id', ([profileExecutionId], schema) => {
      if (transactionTypePositionCheck(profileExecutionId, 1)) {
        return schema.required(m.beneficiary_id_required).matches(/^[a-zA-Z0-9]+$/, m.beneficiary_id_alphanumeric);
      }

      return schema.notRequired().nullable();
    }),

    invoice_period_start_date: Yup.date().when('profile_execution_id', ([profileExecutionId], schema) => {
      if (transactionTypePositionCheck(profileExecutionId, 4)) {
        return schema
          .nullable()
          .test('invoicing-period-start-or-end-required', m.invoicing_period_start_or_end_required, function (value) {
            const { invoice_period_end_date } = this.parent;
            if (value || invoice_period_end_date) return true;

            return false;
          })
          .test('is-before-or-same-as-current-date', m.invoice_period_start_must_be_today_or_earlier, function (value) {
            if (!value) return true;

            return dayjs(value).isBefore(dayjs());
          });
      }

      return schema.notRequired().nullable();
    }),

    invoice_period_end_date: Yup.date().when('profile_execution_id', ([profileExecutionId], schema) => {
      if (transactionTypePositionCheck(profileExecutionId, 4)) {
        return schema
          .nullable()
          .test(
            'is-after-or-same-as-invoice-period-start-date',
            m.invoice_period_end_cannot_be_before_start,
            function (value) {
              const { invoice_period_start_date } = this.parent;
              if (!value || !invoice_period_start_date) return true;

              return dayjs(value).isSameOrAfter(dayjs(invoice_period_start_date), 'day');
            },
          );
      }

      return schema.notRequired().nullable();
    }),

    invoice_period_description: Yup.string().notRequired().nullable(),

    delivery_party_name: Yup.string().notRequired().nullable(),

    delivery_street_name: Yup.string().when('profile_execution_id', ([profileExecutionId], schema) => {
      if (requiresDeliveryAddressFields(profileExecutionId)) {
        return schema.required(m.delivery_street_name_required);
      }

      return schema.notRequired().nullable();
    }),

    delivery_city_name: Yup.string().when('profile_execution_id', ([profileExecutionId], schema) => {
      if (requiresDeliveryAddressFields(profileExecutionId)) {
        return schema.required(m.delivery_city_name_required);
      }

      return schema.notRequired().nullable();
    }),

    delivery_country_code: Yup.mixed()
      .nullable()
      .test('required-when-delivery-profile', m.country_required, function (value) {
        const root = getRootValue(this);
        const profileExecutionId = root?.profile_execution_id;
        if (requiresDeliveryAddressFields(profileExecutionId)) {
          return value != null;
        }

        return true;
      })
      .test('structure-when-delivery-profile', function (value) {
        const root = getRootValue(this);
        const profileExecutionId = root?.profile_execution_id;
        if (requiresDeliveryAddressFields(profileExecutionId)) {
          if (value == null) return true;
          if (typeof value === 'object') {
            const hasCode = !!value['code'];
            const hasLabel = !!value['label'];
            if (!hasCode || !hasLabel) {
              return this.createError({ message: m.country_required });
            }
          }
        }

        return true;
      })
      .test('not-ae-when-export', m.country_code_cannot_be_ae_for_exports, function (value) {
        const root = getRootValue(this);
        const profileExecutionId = root?.profile_execution_id;
        if (isExportProfile(profileExecutionId)) {
          if (!value) return true;
          let countryCode;
          if (typeof value === 'object' && value !== null && 'code' in value) {
            countryCode = value.code;
          } else {
            countryCode = value;
          }
          if (countryCode === 'AE') {
            return this.createError({
              message: m.country_code_cannot_be_ae_for_exports_short,
            });
          }
        }

        return true;
      }),

    delivery_country_subentity: Yup.mixed().when(
      ['profile_execution_id', 'delivery_country_code'],
      ([profileExecutionId, deliveryCountryCode], schema) => {
        if (!requiresDeliveryAddressFields(profileExecutionId)) {
          return schema.notRequired().nullable();
        }

        if (getDeliveryCountryCode(deliveryCountryCode) === UAE_COUNTRY_CODE) {
          return Yup.object()
            .shape({
              code: Yup.string().required(m.delivery_country_subentity_required),
              name: Yup.string().required(),
            })
            .required(m.delivery_country_subentity_required)
            .nullable();
        }

        return Yup.string().required(m.delivery_country_subentity_required);
      },
    ),

    delivery_postal_zone: Yup.string().notRequired().nullable(),
  };
};

export const withAgentBillingPartyDistinctness = (schema, m, isPurchase = false) =>
  schema.test(
    'agent-billing-party-distinct',
    m.agent_billing_same_vat_and_principal,
    createAgentBillingPartyDistinctnessTest(isPurchase),
  );
